// ════════════════════════════════════════════════════════════════════════
//  Übersicht aller Schulen
//
//  Die Liste ist der Einstieg in alles Kaufmännische: Lizenzzustand,
//  Kundennummer, Jahrespreis, wie viele Klassen und Lehrkräfte dranhängen.
//  Was NICHT dasteht und nie dastehen wird: eine Zeile über ein Kind.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@otto/plattform";
import { useCreateSchool, useSchools } from "../lib/data.js";
import { LICENSE_LABEL, formatDate, formatEuro, licenseState } from "../lib/license.js";
import shell from "../../../shared/components/shell.module.css";
import styles from "./schools.module.css";

export function Schools() {
  const { client } = useSession();
  const schools = useSchools(client);
  const create = useCreateSchool(client);
  const [name, setName] = useState("");

  return (
    <>
      <div className={shell.card}>
        <h1 className={shell.title}>Schulen</h1>
        <p className={shell.subtitle}>
          Schulen, Lizenzen und Ansprechpartner. Klassen und Ergebnisse liegen in der
          Verwaltung der jeweiligen Schule – hier sind sie bewusst nicht zu sehen.
        </p>

        <form
          className={styles.newSchool}
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (trimmed === "") return;
            create.mutate(trimmed, { onSuccess: () => setName("") });
          }}
        >
          <div className={styles.grow}>
            <label htmlFor="new-school">Neue Schule anlegen</label>
            <input
              id="new-school"
              className={shell.field}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Grundschule am Markt"
            />
          </div>
          <button
            type="submit"
            className={shell.button}
            disabled={create.isPending || name.trim() === ""}
          >
            {create.isPending ? "Wird angelegt …" : "Anlegen"}
          </button>
        </form>
        <p className={styles.hint}>
          Der Name genügt. Kundennummer, Preis und Lizenz kommen danach – eine Schule
          entsteht oft vor der Vereinbarung.
        </p>

        {create.isError && (
          <div className={`${shell.notice} ${shell.noticeError}`}>
            Die Schule konnte nicht angelegt werden: {create.error.message}
          </div>
        )}
      </div>

      <div className={`${shell.card} ${shell.spaced}`}>
        <div className={styles.tableHead}>
          <h2 className={shell.title} style={{ fontSize: "1.15rem" }}>
            {schools.data ? `${schools.data.length} Schulen` : "Schulen"}
          </h2>
          <Link to="/schools/billing" className={shell.buttonSecondary}>
            Offene Abrechnungen
          </Link>
        </div>

        {schools.isPending && <p className={shell.empty}>Einen Moment …</p>}
        {schools.isError && (
          <div className={`${shell.notice} ${shell.noticeError}`}>
            Die Schulen waren nicht abzurufen: {schools.error.message}
          </div>
        )}
        {schools.data?.length === 0 && (
          <p className={shell.empty}>Noch keine Schule angelegt.</p>
        )}

        {schools.data && schools.data.length > 0 && (
          <div className={styles.scroll}>
            <table className={shell.wide}>
              <thead>
                <tr>
                  {/* Der übrige Platz gehört dem Schulnamen: die Zahlen rechts
                      sollen am Kartenrand stehen bleiben. */}
                  <th className={shell.stretch}>Schule</th>
                  <th>Kundennummer</th>
                  <th>Lizenz bis</th>
                  <th>Zustand</th>
                  <th>Verlängerung</th>
                  <th className={styles.numeric}>Jahrespreis</th>
                  <th className={styles.numeric}>Klassen</th>
                  <th className={styles.numeric}>Lehrkräfte</th>
                </tr>
              </thead>
              <tbody>
                {schools.data.map((school) => {
                  const state = licenseState(school.licenseUntil);
                  return (
                    <tr key={school.id}>
                      <td className={shell.stretch}>
                        <Link to={`/schools/${school.id}`}>{school.name}</Link>
                      </td>
                      <td>{school.internals?.customerNumber ?? "–"}</td>
                      <td>{formatDate(school.licenseUntil)}</td>
                      <td>
                        {/* Der Zustand steht als data-Attribut, die Farbe dazu
                            im CSS-Modul. Eine Bedingung in JSX würde die
                            Gestaltung hierher holen - und die Farbwerte selbst
                            kommen ausschließlich aus @otto/design. */}
                        <span className={styles.state} data-state={state}>
                          {LICENSE_LABEL[state]}
                        </span>
                      </td>
                      <td>{school.autoRenew ? "automatisch" : "manuell"}</td>
                      <td className={styles.numeric}>
                        {formatEuro(school.internals?.annualPriceCents ?? null)}
                      </td>
                      <td className={styles.numeric}>{school.classCount}</td>
                      <td className={styles.numeric}>
                        {school.teacherCount}
                        {school.schoolAdminCount === 0 && (
                          <span className={styles.warn} title="Noch kein Schuladmin">
                            {" "}
                            ohne Leitung
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
